import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from '@legendapp/state/react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { fetchEdgeFunction } from '../lib/api';
import { languages$, fetchLanguages } from '../store/languages$';
import {
  chats$,
  fetchChats,
  createChat,
  addMessage,
  getActiveChat,
  setActiveChat,
  clearActiveChat,
  getChatsAsOptions,
  truncateMessagesFromIndex,
} from '../store/chats$';
import { getCourseConfig, getConfigWithOverride } from '../store/courseConfig$';
import { LEVEL_OPTIONS } from '../components/AddLanguageModal';
import { trackEvent } from '../lib/analytics';
import { logger } from '../lib/logger';
import { useStreamingChat, STREAMING_STATES } from '../hooks/useStreamingChat';
import { readSSEStream } from '../utils/readSSEStream';
import { settings$ } from '../store/settings$';

// Language code to name mapping (for learning language)
const LANGUAGE_NAMES = {
  en: 'English',
  fr: 'French',
  ja: 'Japanese',
  ru: 'Russian',
  'zh-CN': 'Simplified Chinese',
  'zh-TW': 'Traditional Chinese',
  'zh-HK': 'Cantonese',
};

// UI language code to full name mapping (for AI responses)
const UI_LANGUAGE_NAMES = {
  en: 'English',
  zh: 'Chinese',
  ru: 'Russian',
};

// ===== Welcome Cache Helpers =====
const WELCOME_CACHE_KEY = 'mellow_welcome_cache';

const getWelcomeCache = (languageCode) => {
  try {
    const cache = JSON.parse(localStorage.getItem(WELCOME_CACHE_KEY) || '{}');
    return cache[languageCode] || null;
  } catch {
    return null;
  }
};

const setWelcomeCache = (languageCode, content) => {
  try {
    const cache = JSON.parse(localStorage.getItem(WELCOME_CACHE_KEY) || '{}');
    cache[languageCode] = {
      content,
      used: false,
    };
    localStorage.setItem(WELCOME_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Ignore storage errors
  }
};

const markWelcomeCacheUsed = (languageCode) => {
  try {
    const cache = JSON.parse(localStorage.getItem(WELCOME_CACHE_KEY) || '{}');
    if (cache[languageCode]) {
      cache[languageCode].used = true;
      localStorage.setItem(WELCOME_CACHE_KEY, JSON.stringify(cache));
    }
  } catch {
    // Ignore storage errors
  }
};

const ChatContext = createContext(null);

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) throw new Error('useChat must be used within ChatProvider');
  return context;
};

export const ChatProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  // Insufficient credits state - triggers modal open in ChatTab
  const [insufficientCredits, setInsufficientCredits] = useState(false);

  // Pending input - set from other tabs (e.g., Discovery "Ask AI")
  // ChatTab will consume this and clear it
  const [pendingInput, setPendingInput] = useState(null);

  // Welcome state (for empty chat) - keyed by language code for instant switching
  const [welcomeByLang, setWelcomeByLang] = useState({}); // { en: {content, error}, fr: {content, error}, ... }
  const [welcomeLoading, setWelcomeLoading] = useState(false);

  // Subscribe to store state
  const items = useSelector(() => chats$.items.get());
  const activeChatId = useSelector(() => chats$.activeChatId.get());
  const isLoading = useSelector(() => chats$.isLoading.get());
  const selectedLanguage = useSelector(() => languages$.selectedCode.get());
  const languageItems = useSelector(() => languages$.items.get());

  // Get current language info
  const currentLanguageInfo = languageItems?.find(l => l.language_code === selectedLanguage);

  // Get UI language for AI responses (from i18n)
  const uiLanguage = i18n.language || 'en';

  // ===== Streaming infrastructure =====
  const {
    streamingState,
    streamingStateRef,
    streamingMessageIndex,
    isStreamingActive,
    forceCompleteStreaming,
    handleAnimationComplete,
    stopStreaming,
    streamBufferRef,
    visibleLengthRef,
    prepareFetch,
    startStreaming,
    processStreamingResponse,
    cleanupStreaming,
  } = useStreamingChat();

  // Mellow's streaming preference is stored in Supabase via settings$,
  // not in the hook's localStorage. Read it here as the source of truth.
  const mellowStreamingEnabled = useSelector(() => settings$.streamingEnabled.get());

  // Override captureStreamingMode to use settings$ instead of the hook's localStorage.
  // Snapshots the preference at the start of each request so mid-request toggles don't
  // cause a state machine / fetch mode mismatch.
  const captureStreamingMode = useCallback(() => {
    return mellowStreamingEnabled;
  }, [mellowStreamingEnabled]);

  // Wrap stopStreaming to pre-sync truncated content to chats$ BEFORE the state machine
  // transitions to FULLY_COMPLETE. Without this, the state change fires first and React
  // renders renderAIContent(msg.content) which still holds the pre-threshold text (50
  // chars), causing a visible flash before onAbort asynchronously corrects it.
  const stopStreamingWithSync = useCallback(() => {
    const truncatedLen = visibleLengthRef.current;
    const buf = streamBufferRef.current;
    if (buf && truncatedLen > 0 && activeChatId) {
      const truncated = buf.slice(0, truncatedLen);
      const latestItems = chats$.items.get();
      const latestChat = latestItems.find(c => c.id === activeChatId);
      if (latestChat?.messages?.length > 0 &&
          latestChat.messages[latestChat.messages.length - 1].role === 'assistant') {
        const updatedMessages = [...latestChat.messages];
        updatedMessages[updatedMessages.length - 1] = {
          ...updatedMessages[updatedMessages.length - 1],
          content: truncated,
        };
        chats$.items.set(latestItems.map(c =>
          c.id === activeChatId ? { ...c, messages: updatedMessages } : c
        ));
      }
    }
    stopStreaming();
  }, [stopStreaming, activeChatId, streamBufferRef, visibleLengthRef]);


  // Fetch languages on mount (so chat knows which language to load)
  useEffect(() => {
    fetchLanguages();
  }, []);

  // Fetch chats when language changes
  useEffect(() => {
    if (selectedLanguage) {
      fetchChats(selectedLanguage);
    } else {
      // No language selected yet - not loading, just empty
      chats$.isLoading.set(false);
    }
  }, [selectedLanguage]);

  // Load welcome from localStorage cache immediately when language changes (instant display)
  useEffect(() => {
    if (!selectedLanguage) return;

    // Skip if already in memory for this language
    if (welcomeByLang[selectedLanguage]) return;

    // Try to load from localStorage cache
    const cached = getWelcomeCache(selectedLanguage);
    if (cached?.content) {
      setWelcomeByLang(prev => ({
        ...prev,
        [selectedLanguage]: { content: cached.content, error: null }
      }));
    }
  }, [selectedLanguage, welcomeByLang]);

  // Get active chat data
  const activeChat = items?.find(c => c.id === activeChatId) || null;
  const messages = activeChat?.messages || [];
  const messageSources = activeChat?.message_sources || {};

  // Derived: is on new chat screen (no chat selected or empty messages)
  const isOnNewChatScreen = !activeChatId || messages.length === 0;

  // Derived: welcome content and error for current language
  const welcomeContent = selectedLanguage ? (welcomeByLang[selectedLanguage]?.content || null) : null;
  const welcomeError = selectedLanguage ? (welcomeByLang[selectedLanguage]?.error || null) : null;

  // Helper: Wait for language to be selected (with timeout)
  const waitForLanguage = useCallback(async (maxWaitMs = 3000) => {
    const startTime = Date.now();

    while (Date.now() - startTime < maxWaitMs) {
      const lang = languages$.selectedCode.get();
      const loading = languages$.isLoading.get();

      if (lang) return lang; // Language is ready
      if (!loading) return null; // Not loading anymore but no language = user has no languages

      // Still loading, wait a bit
      await new Promise(r => setTimeout(r, 100));
    }

    return languages$.selectedCode.get(); // Return whatever we have after timeout
  }, []);

  // Send message to AI
  // For regular chat: sendMessage(text)
  // For lookups: sendMessage(text, { mode: 'dictionary', source: {...} })
  // For course with override: sendMessage(text, { mode: 'course', courseConfigOverride: {...} })
  const sendMessage = useCallback(async (text, options = {}) => {
    // Read streaming state from ref (not closure) to avoid stale boolean.
    // Only block while the network fetch is active. Animation phases (NETWORK_COMPLETE,
    // DISPLAYING_RESULT) no longer block input — the button shows Send, users can type.
    if (!text.trim() || sending || streamingStateRef.current === STREAMING_STATES.NETWORK_STREAMING) return;

    const { mode = 'chat', source = null, courseConfigOverride = null } = options;

    setError(null);
    setSending(true);

    // Snapshot streaming preference for this entire request lifecycle
    const isStreaming = captureStreamingMode();
    const controller = prepareFetch();

    try {
      // Wait for language to be available if still loading
      let currentLanguage = selectedLanguage;
      if (!currentLanguage) {
        currentLanguage = await waitForLanguage();
        if (!currentLanguage) {
          throw new Error('No language selected. Please add a language first.');
        }
      }

      let chatId = activeChatId;

      const userMessageText = text;

      // Verify chat exists in current items (prevents stale ID from language switch)
      const currentItems = chats$.items.get();
      const chatExists = chatId && currentItems.some(c => c.id === chatId);

      // Create new chat if none active OR chat doesn't exist in current language
      if (!chatId || !chatExists) {
        const newChat = await createChat(currentLanguage, userMessageText, mode === 'course' ? 'course' : null);
        chatId = newChat.id;

        // Track first chat started (activation event)
        const existingChats = currentItems.filter(c => c.language_code === currentLanguage);
        if (existingChats.length === 0) {
          trackEvent('first_chat_started', {
            language: currentLanguage,
            mode: mode || 'chat',
          });
        }

        // Track course started
        if (mode === 'course') {
          const config = courseConfigOverride || getCourseConfig();
          trackEvent('course_started', {
            topic: userMessageText,
            language_code: currentLanguage,
            learning_style: config?.learningStyle?.join(',') || '',
            communication_style: config?.communicationStyle?.join(',') || '',
          });
        }

        // If source provided, update the new chat's message_sources
        if (source) {
          const { error: srcErr } = await supabase
            .from('mellow_chats')
            .update({ message_sources: { 0: source } })
            .eq('id', chatId);
          if (!srcErr) {
            chats$.items.set(chats$.items.get().map(c =>
              c.id === chatId ? { ...c, message_sources: { 0: source } } : c
            ));
          }
        }
      } else {
        // Add user message to existing chat (with source if provided)
        await addMessage(chatId, { role: 'user', content: userMessageText, timestamp: Date.now() }, source);
      }

      // Track AI chat message sent
      trackEvent('ai_chat_message_sent', {
        mode: mode || 'chat',
        language_code: currentLanguage,
        message_length: userMessageText.length,
        has_source: Boolean(source),
      });

      // Get updated messages for API call (after user message is in store)
      const chat = chats$.items.get().find(c => c.id === chatId);
      const chatMessages = chat?.messages || [];

      // For lookup modes, don't include the lookup text in messages - it goes as lookupText
      const apiMessages = mode === 'chat'
        ? chatMessages.map(m => ({ role: m.role, content: m.content }))
        : chatMessages.slice(0, -1).map(m => ({ role: m.role, content: m.content }));

      const userLevel = currentLanguageInfo?.level || 'beginner';
      const levelInfo = LEVEL_OPTIONS.find(l => l.value === userLevel);
      const levelDescription = levelInfo?.description || userLevel;

      // Build request body
      const requestBody = {
        messages: apiMessages,
        languageContext: {
          code: currentLanguage,
          name: LANGUAGE_NAMES[currentLanguage] || currentLanguage,
          level: levelInfo?.label || 'Beginner',
          levelDescription: levelDescription,
          nativeLang: UI_LANGUAGE_NAMES[uiLanguage] || 'English',
        },
        sessionId: chatId,
        initialMode: chat?.initial_mode || null,
        // Signal server to stream or not based on captured preference
        stream: isStreaming,
      };

      // Add mode and lookupText for lookup modes
      if (mode !== 'chat') {
        requestBody.mode = mode;
        requestBody.lookupText = text;
      }

      // Add course config for course mode chats
      if (mode === 'course' || chat?.initial_mode === 'course') {
        requestBody.courseConfig = courseConfigOverride
          ? getConfigWithOverride(courseConfigOverride)
          : getCourseConfig();
      }

      // Initialize state machine — assistant message will land at chatMessages.length
      const assistantMessageIndex = chatMessages.length;
      startStreaming(assistantMessageIndex);

      const startTime = Date.now();
      const response = await fetchEdgeFunction('mellow-ai-chat', requestBody, {
        signal: controller.signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        if (response.status === 402 && errorData.code === 'INSUFFICIENT_CREDITS') {
          trackEvent('credits_depleted', {
            balance: errorData.balance || 0,
            mode: mode || 'chat',
            language_code: currentLanguage,
          });
          setInsufficientCredits(true);
          setSending(false);
          cleanupStreaming();
          return;
        }

        throw new Error(errorData.error || `Request failed: ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      const isSSE = contentType?.includes('text/event-stream');

      // ===== STREAMING PATH =====
      if (isStreaming && isSSE) {
        let assistantCreated = false;

        await processStreamingResponse(response, {
          updateMessage(content) {
            // Re-fetch items each call so we don't operate on stale state
            const latestItems = chats$.items.get();
            const latestChat = latestItems.find(c => c.id === chatId);
            if (!latestChat) return null;

            const newMsg = { role: 'assistant', content, timestamp: Date.now() };

            if (!assistantCreated) {
              // First call: add the assistant message to chats$ in memory (no DB write)
              assistantCreated = true;
              const newMessages = [...(latestChat.messages || []), newMsg];
              chats$.items.set(latestItems.map(c =>
                c.id === chatId ? { ...c, messages: newMessages } : c
              ));
            } else {
              // Subsequent calls (onDone): update last message content
              const updatedMessages = [...(latestChat.messages || [])];
              if (updatedMessages.length > 0 &&
                  updatedMessages[updatedMessages.length - 1].role === 'assistant') {
                updatedMessages[updatedMessages.length - 1] = {
                  ...updatedMessages[updatedMessages.length - 1],
                  content,
                };
                chats$.items.set(latestItems.map(c =>
                  c.id === chatId ? { ...c, messages: updatedMessages } : c
                ));
              }
            }
            return newMsg;
          },

          setSending,

          onDone(data, content) {
            const latencyMs = Date.now() - startTime;
            trackEvent('ai_response_received', {
              mode: mode || 'chat',
              language_code: currentLanguage,
              response_length: content.length,
              latency_ms: latencyMs,
              streaming: true,
            });
            // Persist to Supabase immediately — decoupled from animation lifecycle.
            // updateMessage (called just before this in processStreamingResponse) has
            // already synced the full content to chats$ in-memory, so we just need
            // to write whatever chats$ now holds for this chat.
            // This guarantees persistence even if the user switches tabs/chats before
            // the animation completes (no more FULLY_COMPLETE deferred write needed).
            const currentItems = chats$.items.get();
            const latestChat = currentItems.find(c => c.id === chatId);
            if (latestChat?.messages?.length > 0 &&
                latestChat.messages[latestChat.messages.length - 1].role === 'assistant') {
              const updatedAt = new Date().toISOString();
              supabase.from('mellow_chats').update({
                messages: latestChat.messages,
                updated_at: updatedAt,
              }).eq('id', chatId).then(({ error: dbErr }) => {
                if (dbErr) logger.error('Failed to persist streaming message', { error: dbErr.message, chatId });
              });
            }
          },

          onAbort(content) {
            // Persist partial content immediately — no animation to race with
            if (content && assistantCreated) {
              const latestItems = chats$.items.get();
              const latestChat = latestItems.find(c => c.id === chatId);
              if (latestChat?.messages?.length > 0 &&
                  latestChat.messages[latestChat.messages.length - 1].role === 'assistant') {
                const finalMessages = [...latestChat.messages];
                finalMessages[finalMessages.length - 1] = {
                  ...finalMessages[finalMessages.length - 1],
                  content,
                };
                const updatedAt = new Date().toISOString();
                chats$.items.set(latestItems.map(c =>
                  c.id === chatId ? { ...c, messages: finalMessages, updated_at: updatedAt } : c
                ));
                supabase.from('mellow_chats').update({
                  messages: finalMessages,
                  updated_at: updatedAt,
                }).eq('id', chatId).then(({ error: dbErr }) => {
                  if (dbErr) logger.error('Failed to persist aborted message', { error: dbErr.message, chatId });
                });
              }
            }
            setSending(false);
          },

          onError(err, content) {
            setError(err.message);
            if (content && assistantCreated) {
              const latestItems = chats$.items.get();
              const latestChat = latestItems.find(c => c.id === chatId);
              if (latestChat?.messages?.length > 0 &&
                  latestChat.messages[latestChat.messages.length - 1].role === 'assistant') {
                const finalMessages = [...latestChat.messages];
                finalMessages[finalMessages.length - 1] = {
                  ...finalMessages[finalMessages.length - 1],
                  content,
                };
                const updatedAt = new Date().toISOString();
                chats$.items.set(latestItems.map(c =>
                  c.id === chatId ? { ...c, messages: finalMessages, updated_at: updatedAt } : c
                ));
                supabase.from('mellow_chats').update({
                  messages: finalMessages,
                  updated_at: updatedAt,
                }).eq('id', chatId).then(({ error: dbErr }) => {
                  if (dbErr) logger.error('Failed to persist error message', { error: dbErr.message, chatId });
                });
              }
            }
            setSending(false);
          },
        });
        return;
      }

      // ===== NON-STREAMING PATH =====
      // IMPORTANT: read the response body BEFORE calling cleanupStreaming().
      // cleanupStreaming() calls abortControllerRef.current.abort(), which kills
      // the fetch body stream. If called first, response.json() would throw AbortError
      // (silently swallowed in catch), dropping the AI response entirely.
      const data = await response.json();
      const latencyMs = Date.now() - startTime;

      // Now safe to reset state machine — body is already fully consumed.
      cleanupStreaming();

      trackEvent('ai_response_received', {
        mode: mode || 'chat',
        language_code: currentLanguage,
        response_length: data.content?.length || 0,
        latency_ms: latencyMs,
        streaming: false,
      });

      await addMessage(chatId, {
        role: 'assistant',
        content: data.content,
        timestamp: Date.now(),
      });

    } catch (err) {
      if (err.name === 'AbortError') {
        // Handled by onAbort callback
      } else {
        logger.error('Chat error', { error: err.message, chatId: activeChatId });
        setError(err.message);
        cleanupStreaming();
      }
    } finally {
      setSending(false);
    }
  }, [
    activeChatId,
    selectedLanguage,
    currentLanguageInfo,
    uiLanguage,
    sending,
    captureStreamingMode,
    prepareFetch,
    startStreaming,
    processStreamingResponse,
    cleanupStreaming,
    waitForLanguage,
  ]);

  // Dictionary lookup - just send mode + text, backend handles the prompt
  const lookupDictionary = useCallback((word) => {
    sendMessage(word, {
      mode: 'dictionary',
      source: { type: 'dictionary', quoteText: word }
    });
  }, [sendMessage]);

  // Grammar analysis
  const lookupGrammar = useCallback((sentence) => {
    sendMessage(sentence, {
      mode: 'grammar',
      source: { type: 'grammar', quoteText: sentence }
    });
  }, [sendMessage]);

  // Native expression
  const lookupNative = useCallback((text) => {
    sendMessage(text, {
      mode: 'native',
      source: { type: 'native', quoteText: text }
    });
  }, [sendMessage]);

  // Correction (proofread a piece of user-written text)
  const lookupCorrection = useCallback((text) => {
    sendMessage(text, {
      mode: 'correction',
      source: { type: 'correction', quoteText: text }
    });
  }, [sendMessage]);

  // Course mode
  const startCourse = useCallback((topic) => {
    sendMessage(topic, {
      mode: 'course',
      source: { type: 'course', quoteText: topic }
    });
  }, [sendMessage]);

  // Course mode with temporary config override (for welcome panel suggestions)
  const startCourseWithConfig = useCallback((topic, configOverride) => {
    sendMessage(topic, {
      mode: 'course',
      source: { type: 'course', quoteText: topic },
      courseConfigOverride: configOverride
    });
  }, [sendMessage]);

  // Internal function to fetch welcome from server
  const fetchWelcomeFromServer = useCallback(async () => {
    const userLevel = currentLanguageInfo?.level || 'beginner';
    const levelInfo = LEVEL_OPTIONS.find(l => l.value === userLevel);

    const requestBody = {
      messages: [],
      mode: 'welcome',
      languageContext: {
        code: selectedLanguage,
        name: LANGUAGE_NAMES[selectedLanguage] || selectedLanguage,
        level: levelInfo?.label || 'Beginner',
        levelDescription: levelInfo?.description || userLevel,
        nativeLang: UI_LANGUAGE_NAMES[uiLanguage] || 'English',
      },
    };

    const response = await fetchEdgeFunction('mellow-ai-chat', requestBody);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Request failed: ${response.status}`);
    }

    // The server streams welcome content when streaming is enabled (Fix 2B).
    // Detect SSE response and consume it as a stream rather than calling response.json().
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('text/event-stream')) {
      let content = '';
      await readSSEStream(response, {
        onChunk(delta) { content += delta; },
        onDone() {},
      });
      return content;
    }

    const data = await response.json();
    return data.content;
  }, [selectedLanguage, currentLanguageInfo, uiLanguage]);

  // Fetch welcome: check cache first, then fetch if needed
  const fetchWelcome = useCallback(async (forceRefresh = false) => {
    if (welcomeLoading || !selectedLanguage) return;

    if (!forceRefresh && welcomeByLang[selectedLanguage]?.content) {
      return;
    }

    if (!forceRefresh) {
      const cached = getWelcomeCache(selectedLanguage);
      if (cached?.content) {
        setWelcomeByLang(prev => ({
          ...prev,
          [selectedLanguage]: { content: cached.content, error: null }
        }));
        return;
      }
    }

    setWelcomeLoading(true);

    try {
      const content = await fetchWelcomeFromServer();
      setWelcomeByLang(prev => ({
        ...prev,
        [selectedLanguage]: { content, error: null }
      }));
      setWelcomeCache(selectedLanguage, content);
    } catch (err) {
      logger.error('Welcome fetch error', { error: err.message, language: selectedLanguage });
      setWelcomeByLang(prev => ({
        ...prev,
        [selectedLanguage]: { content: null, error: err.message }
      }));
    } finally {
      setWelcomeLoading(false);
    }
  }, [selectedLanguage, welcomeLoading, welcomeByLang, fetchWelcomeFromServer]);

  // Mark welcome as used and prefetch new one in background
  const markWelcomeUsedAndPrefetch = useCallback(() => {
    if (!selectedLanguage) return;

    markWelcomeCacheUsed(selectedLanguage);

    fetchWelcomeFromServer()
      .then(content => {
        setWelcomeByLang(prev => ({
          ...prev,
          [selectedLanguage]: { content, error: null }
        }));
        setWelcomeCache(selectedLanguage, content);
      })
      .catch(err => {
        logger.error('Background welcome prefetch failed', { error: err.message, language: selectedLanguage });
      });
  }, [selectedLanguage, fetchWelcomeFromServer]);

  // Start new chat — also cleans up any in-progress streaming
  const startNewChat = useCallback(() => {
    // Soft cleanup: don't abort the fetch — let any in-flight stream complete in the
    // background so its full content is persisted. State machine resets immediately so
    // the new chat view is interactive.
    cleanupStreaming(true);
    setSending(false); // clear sending indicator immediately for the new chat view

    if (isOnNewChatScreen) {
      if (selectedLanguage) {
        setWelcomeByLang(prev => ({
          ...prev,
          [selectedLanguage]: { content: null, error: null }
        }));
      }
      fetchWelcome(true);
    } else {
      clearActiveChat();
    }
  }, [isOnNewChatScreen, selectedLanguage, fetchWelcome, cleanupStreaming]);

  // Select a chat — also cleans up any in-progress streaming
  const selectChat = useCallback((chatId) => {
    // Soft cleanup: don't abort the fetch — let any in-flight stream complete in the
    // background so its full content is persisted. State machine resets immediately so
    // the new chat view is interactive. setSending clears the typing indicator at once.
    cleanupStreaming(true);
    setSending(false); // clear sending indicator immediately for the new chat view
    setActiveChat(chatId);
  }, [cleanupStreaming]);

  // Get dropdown options
  const chatOptions = getChatsAsOptions();

  // Clear insufficient credits flag (called when modal opens)
  const clearInsufficientCredits = useCallback(() => {
    setInsufficientCredits(false);
  }, []);

  // Retry from a specific AI message index.
  // Truncates the user message + AI message pair and re-sends the user message.
  const retryFromMessage = useCallback(async (aiMessageIndex) => {
    const chatId = activeChatId;
    if (!chatId) return;

    const confirmed = window.confirm(
      'All future messages will be lost without history version. Are you sure?'
    );
    if (!confirmed) return;

    try {
      setError(null);

      const chat = chats$.items.get().find(c => c.id === chatId);
      if (!chat?.messages) throw new Error('Chat not found');

      // The user message that triggered this AI response is at aiMessageIndex - 1
      const userMessageIndex = aiMessageIndex - 1;
      if (userMessageIndex < 0) throw new Error('Cannot retry the first message');

      const userMsg = chat.messages[userMessageIndex];
      if (!userMsg || userMsg.role !== 'user') throw new Error('User message not found before AI response');

      const source = chat.message_sources?.[userMessageIndex] || null;

      // Truncate from the user message index (removes user message + AI response + everything after)
      await truncateMessagesFromIndex(chatId, userMessageIndex);

      // Determine mode from source
      let retryMode = 'chat';
      if (source?.type === 'dictionary') retryMode = 'dictionary';
      else if (source?.type === 'grammar') retryMode = 'grammar';
      else if (source?.type === 'native') retryMode = 'native';
      else if (source?.type === 'correction') retryMode = 'correction';
      else if (source?.type === 'course') retryMode = 'course';

      // Re-send — sendMessage will re-add the user message and get a new AI response
      await sendMessage(userMsg.content, { mode: retryMode, source });
    } catch (err) {
      logger.error('Retry error', { error: err.message, chatId: activeChatId });
      setError(err.message);
    }
  }, [activeChatId, sendMessage]);

  // Delete from a specific user message index
  const deleteFromMessage = useCallback(async (userMessageIndex) => {
    const chatId = activeChatId;
    if (!chatId) return;

    const confirmed = window.confirm(
      'This message and all messages below will be deleted. Are you sure?'
    );
    if (!confirmed) return;

    try {
      await truncateMessagesFromIndex(chatId, userMessageIndex);
    } catch (err) {
      logger.error('Delete error', { error: err.message, chatId, userMessageIndex });
      setError(err.message);
    }
  }, [activeChatId]);

  return (
    <ChatContext.Provider value={{
      messages,
      messageSources,
      sending,
      error,
      isLoading,
      activeChatId,
      activeChat,
      chatOptions,
      sendMessage,
      startNewChat,
      selectChat,
      // Learning language (for cards)
      selectedLanguage,
      // Dict/Grammar/Native/Correction/Course lookup
      lookupDictionary,
      lookupGrammar,
      lookupNative,
      lookupCorrection,
      startCourse,
      startCourseWithConfig,
      // Welcome state
      welcomeContent,
      welcomeLoading,
      welcomeError,
      fetchWelcome,
      markWelcomeUsedAndPrefetch,
      isOnNewChatScreen,
      // Credits
      insufficientCredits,
      clearInsufficientCredits,
      // Pending input (from other tabs like Discovery)
      pendingInput,
      setPendingInput,
      // Message actions
      retryFromMessage,
      deleteFromMessage,
      // Streaming state (for ChatTab rendering)
      streamingState,
      streamingMessageIndex,
      isStreamingActive,
      forceCompleteStreaming,
      handleAnimationComplete,
      stopStreaming: stopStreamingWithSync,
      streamBufferRef,
      visibleLengthRef,
      // Cleanup — also called by ChatTab on unmount (Plan B)
      cleanupStreaming,
    }}>
      {children}
    </ChatContext.Provider>
  );
};
